/**
 * P2d — Admin Branding editor.
 *
 * GET+PUT /api/branding — single-row settings.
 */
import React, { useEffect, useState } from "react";
import { Section, Card, Button, Alert, Input, Label } from "../design-system";
import { useAuth } from "../auth/AuthProvider";


export default function AdminBrandingPage() {
  const { apiGet } = useAuth();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { setB(await apiGet("/api/branding")); }
      catch (e) { setErr(e?.message || String(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const save = async () => {
    setErr(null); setOk(null); setSaving(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API}/api/branding`, {
        method: "PUT",
        headers: { "Content-Type": "application/json",
                   Authorization: `Bearer ${localStorage.getItem("access_token")}` },
        body: JSON.stringify({
          app_name: b.app_name || null,
          logo_main_url: b.logo_main_url || null,
          logo_compact_url: b.logo_compact_url || null,
          favicon_url: b.favicon_url || null,
          color_primary: b.color_primary || null,
          color_secondary: b.color_secondary || null,
          color_accent: b.color_accent || null,
          login_background_url: b.login_background_url || null,
          company_info: b.company_info || null,
          contacts: b.contacts || null,
        }),
      });
      if (!resp.ok) throw new Error(`status_${resp.status}`);
      setB(await resp.json());
      setOk("Branding aggiornato. Ricarica per applicare i nuovi valori all'header.");
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setSaving(false); }
  };

  const patch = (k, v) => setB(prev => ({ ...prev, [k]: v }));
  const patchObj = (root, k, v) =>
    setB(prev => ({ ...prev, [root]: { ...(prev?.[root] || {}), [k]: v } }));

  if (!b) return <Section title="Branding" data-testid="admin-branding-page">
    {err ? <Alert variant="danger">{err}</Alert> : <p className="muted">Caricamento…</p>}
  </Section>;

  return (
    <Section title="Branding editor"
             subtitle="Nome portale, logo, colori tema, informazioni azienda"
             data-testid="admin-branding-page">
      {err && <Alert variant="danger">{err}</Alert>}
      {ok && <Alert tone="success">{ok}</Alert>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card>
          <Card.Header><strong>Identità</strong></Card.Header>
          <Card.Body>
            <div style={{ marginBottom: 8 }}>
              <Label>Nome portale</Label>
              <Input value={b.app_name || ""} data-testid="brand-app-name"
                     onChange={(e) => patch("app_name", e.target.value)} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>Logo principale (URL)</Label>
              <Input value={b.logo_main_url || ""} data-testid="brand-logo-main"
                     onChange={(e) => patch("logo_main_url", e.target.value)} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>Logo compatto (URL, header)</Label>
              <Input value={b.logo_compact_url || ""} data-testid="brand-logo-compact"
                     onChange={(e) => patch("logo_compact_url", e.target.value)} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>Favicon (URL)</Label>
              <Input value={b.favicon_url || ""}
                     onChange={(e) => patch("favicon_url", e.target.value)} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>Sfondo login (URL)</Label>
              <Input value={b.login_background_url || ""}
                     onChange={(e) => patch("login_background_url", e.target.value)} />
            </div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Header><strong>Colori</strong></Card.Header>
          <Card.Body>
            <div style={{ marginBottom: 8 }}>
              <Label>Primario</Label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="color" value={b.color_primary || "#0F4C81"}
                       onChange={(e) => patch("color_primary", e.target.value)}
                       data-testid="brand-color-primary"
                       style={{ width: 44, height: 32, cursor: "pointer" }} />
                <Input value={b.color_primary || ""}
                       onChange={(e) => patch("color_primary", e.target.value)} />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>Secondario</Label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="color" value={b.color_secondary || "#F2A65A"}
                       onChange={(e) => patch("color_secondary", e.target.value)}
                       style={{ width: 44, height: 32, cursor: "pointer" }} />
                <Input value={b.color_secondary || ""}
                       onChange={(e) => patch("color_secondary", e.target.value)} />
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>Accento</Label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="color" value={b.color_accent || "#1D6E5F"}
                       onChange={(e) => patch("color_accent", e.target.value)}
                       style={{ width: 44, height: 32, cursor: "pointer" }} />
                <Input value={b.color_accent || ""}
                       onChange={(e) => patch("color_accent", e.target.value)} />
              </div>
            </div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Header><strong>Anagrafica azienda</strong></Card.Header>
          <Card.Body>
            <div style={{ marginBottom: 8 }}>
              <Label>Ragione sociale</Label>
              <Input value={b.company_info?.legal_name || ""}
                     onChange={(e) => patchObj("company_info", "legal_name", e.target.value)} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>P.IVA</Label>
              <Input value={b.company_info?.vat || ""}
                     onChange={(e) => patchObj("company_info", "vat", e.target.value)} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>Indirizzo</Label>
              <Input value={b.company_info?.address || ""}
                     onChange={(e) => patchObj("company_info", "address", e.target.value)} />
            </div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Header><strong>Contatti</strong></Card.Header>
          <Card.Body>
            <div style={{ marginBottom: 8 }}>
              <Label>Email</Label>
              <Input type="email" value={b.contacts?.email || ""}
                     onChange={(e) => patchObj("contacts", "email", e.target.value)} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>Telefono</Label>
              <Input value={b.contacts?.phone || ""}
                     onChange={(e) => patchObj("contacts", "phone", e.target.value)} />
            </div>
          </Card.Body>
        </Card>
      </div>
      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button variant="primary" onClick={save} loading={saving} disabled={saving}
                data-testid="brand-save">{saving ? "Salvataggio…" : "Salva branding"}</Button>
      </div>
    </Section>
  );
}
